import { useEffect, useState } from "react";

/**
 * Whether a CSS media query currently matches, as state.
 *
 * For the cases a Tailwind breakpoint class cannot cover: not "style this
 * differently at that width" but "at that width, do not mount this at all".
 * A portalled panel is the example — it escapes its container, so hiding its
 * trigger with `xl:hidden` would leave the panel itself on screen, floating
 * over a layout that no longer has anything to attach it to.
 *
 * Guarded against a server or a test environment with no `matchMedia`, and
 * seeded from the real value on first render so nothing flashes into place.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const list = window.matchMedia(query);
    // The query can change between renders, so re-read rather than trust the
    // value the previous one seeded.
    setMatches(list.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
